import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import { FiSave } from 'react-icons/fi'
import { GrEdit, GrTrash } from 'react-icons/gr'
import AppContext from 'context'
import NumDisplay from 'components/num-display'
import ToolTip from 'components/tooltip'
import colors from 'utils/colors'
import mixins from 'utils/mixins'
import WP_DATA from 'utils/wp_data'
import { keyExists, moneyFormat } from 'utils/helpers'

const LightBackground = styled.div`
  ${mixins.lightBackground}
`

const Edit = styled.button`
  display: none;
  position: absolute;
  top: -24px;
  right: -24px;
  border-radius: 50px;
  background-color: rgba(0,0,0,.14);
  color: black;
  &:hover {
    color: white;
    background-color: ${colors.blue};
  }
`

const FieldSet = styled.div`
  ${mixins.card}
  border-top: 2px solid ${props => props.sectionId === 'Liabilities' ? colors.red : colors.blue};
  position: relative;
  height: auto;
  &:hover ${Edit} {
    display: block;
  }
`

const Legend = styled.legend`
  display: flex;
  justify-content: flex-start;
`

const Header = styled.div`
  cursor: pointer;
`

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`

const Controls = styled.div`
  border-top: .5px solid ${colors.grey};
  padding: 30px 0;
  margin-top: 30px;
  button:disabled {
    opacity: 0.7;
  }
`

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  label {
    display: flex;
    flex-direction: column;
  }
`
// display: grid;
// grid-template-columns: 1fr 1fr;
// grid-gap: 60px;

const Delete = styled.button`
  background: red;
  color: white;
  margin: 60px auto 0; 
  display: block;
  width: auto;
`

export default ({ sectionId, id, fields }) => {
  const { dispatch, save } = useContext(AppContext)
  const [open, setOpen] = useState(false)
  const [newCardTitle, setNewCardTitle] = useState('')
  const {
    self,
    spouse,
    joint,
    notes,
    // Life Insurance Cash Value
    death_benefit,
    // Liabilities
    interest_rate,
    years_remaining,
  } = fields
  const isDefaultField = WP_DATA.default_keys.includes(id)
  const rowTotal = self + spouse + joint

  const handleInputChange = e => {
    let { name, value, type } = e.target || e
    if (!value && type === 'number')
      value = 0
    else if (type === 'number')
      value = parseFloat(value)
    dispatch({
      type: 'CALC_SECTION',
      payload: { sectionId, id, name, value }
    })
  }

  const toggleAndSave = () => {
    if (open)
      save()
    setOpen(!open)
  }

  return (
    <FieldSet sectionId={sectionId}>
      <Edit onClick={toggleAndSave} type="button"> 
        {open ? <FiSave /> : <GrEdit />}
      </Edit>

      <Legend>
        <h3 style={{marginTop:5, marginRight: 5}}>{id}</h3>
        {Object.keys(WP_DATA.DESCRIPTIONS).includes(id) && (
          <ToolTip title={id}>{WP_DATA.DESCRIPTIONS[id]}</ToolTip>
        )}
      </Legend>

      <Header onClick={toggleAndSave}>
        <HeaderGrid>
          <NumDisplay
            bigNum={moneyFormat.to(self)}
            heading="self"
            style={{textAlign:'center'}}
          />
          <NumDisplay
            bigNum={moneyFormat.to(spouse)}
            heading="spouse"
            style={{textAlign:'center'}}
          />
          <NumDisplay
            bigNum={moneyFormat.to(joint)}
            heading="joint"
            style={{textAlign:'center'}}
          />
        </HeaderGrid>

        <LightBackground>
          <NumDisplay
            bigNum={moneyFormat.to(rowTotal)}
            heading="total"
            style={{textAlign:'center'}}
          />
        </LightBackground>

        {keyExists('interest_rate', fields) && interest_rate > 0 && (
          <p><strong>Interest Rate:</strong> {interest_rate}%</p>
        )}
        {keyExists('years_remaining', fields) && years_remaining > 0 && (
          <p><strong>Years Remaining:</strong> {years_remaining}</p>
        )}

        {keyExists('death_benefit', fields) && death_benefit > 0 && (
          <p><strong>Death benefit:</strong> {moneyFormat.to(death_benefit)}</p>
        )}

        {notes && (
          <p style={{ fontSize: 14 }}>
            <strong>Notes</strong><br/>
            {notes}
          </p>
        )}
      </Header>
      {open && (
        <Controls>
          <Grid>
              <label>
                Self
                <input
                  name="self"
                  value={self}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <label>
                Spouse  
                <input
                  name="spouse"
                  value={spouse}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <label>
                Joint
                <input
                  name="joint"
                  value={joint}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
            <div>
              {keyExists('death_benefit', fields) && (
              <label>
                  Death Benefit
                  <input
                    name="death_benefit"
                    value={death_benefit}
                    onChange={handleInputChange}
                    type="number"
                  />
                </label>
              )}
              {keyExists('interest_rate', fields) && (
              <label>
                  Interest Rate %
                  <input
                    name="interest_rate"
                    value={interest_rate}
                    onChange={handleInputChange}
                    type="number"
                  />
                </label>
              )}
              {keyExists('years_remaining', fields) && (
              <label>
                  Years Remaining
                  <input
                    name="years_remaining"
                    value={years_remaining}
                    onChange={handleInputChange}
                    type="number"
                  />
                </label>
              )}
            </div>
          </Grid>
          <br/>

          <textarea
            name="notes"
            value={notes}
            onChange={handleInputChange}
            placeholder="Notes..."
          />

          {!isDefaultField && (
            <>
              <label>
                <strong>Title</strong><br/>
                <input
                  name="years_remaining"
                  value={!!newCardTitle ? newCardTitle : id}
                  onChange={e => setNewCardTitle(e.target.value)}
                  type="text"
                />
              </label>
              <button 
                disabled={id === newCardTitle || newCardTitle === ''}
                onClick={e => {
                  e.stopPropagation()
                  dispatch({ 
                    type: 'UPDATE_CARD_TITLE', 
                    payload: { 
                      sectionId, 
                      id,
                      newCardTitle,
                    } 
                  })
                  setNewCardTitle('')
                  save()
                }
              }>
                Update Title
              </button>
              <br/>
              <Delete 
                onClick={() => dispatch({ type: 'DELETE', payload: { sectionId, id }})}
                type="button" 
              >
                <GrTrash color="white" /> Delete {id}
              </Delete>
            </>
          )}
        </Controls>
      )}
    </FieldSet>
  )
}

